import {Icon} from '../ui/Icon';
import {ContactProfileOverview} from './ContactProfileOverview';
import type {ContactPreferenceSnapshot} from './ContactLifecycle';
import {CorrespondenceHistory} from './CorrespondenceHistory';
import {OrganisationProcessingDefault} from './OrganisationProcessingDefault';
import {ContactIdentityEditor} from './ContactIdentityEditor';
import {useCapability} from '../ui/StaffSession';
import {ManualMobilisationContact} from './ManualMobilisationContact';
import {ContactLifecycle} from './ContactLifecycle';
import { useEffect, useMemo, useState } from "react";
import {useParams, useSearchParams} from "react-router";
import {Link} from "../ui/ScopedLink";
import {withBuildingScope} from "../ui/building-scope";
import { api } from "../api";
import { DateOnlyInput } from "../components/DateOnlyInput";
import { formatDateOnly, formatDateTime } from "../dates";
import { Empty, ErrorState, Loading, State } from "../ui/State";
import { RoleProposalForm } from "./RoleProposalForm";
import { TimelinePanel } from "./TimelinePanel";
import { RelatedDocuments } from "./RelatedDocuments";

type RecordType = "person" | "organisation" | "premises";
type Contact = {
  id: string;
  contact_kind: "Email" | "Telephone"; original_value: string;
  purpose: string; status: string;
  is_primary: boolean;
  supersedes_contact_point_id?: string;
};
type PartyRole = {
  legal_names?:string[];
  development_id?:string;
  premises_id?:string;
  id: string;
  role_code: string;
  role_label: string;
  category: string;
  status: string;
  development_name?: string;
  premises_name?: string;
  start_date?: string;
  end_date?: string;
};
type Membership = {
  id: string;
  membership_role: string;
  status: string;
  organisation_name?: string;
  person_name?: string;
  start_date?: string;
  end_date?: string;
};
type Representation = {
  id: string;
  authority_nature: string;
  status: string;
  representative_name?: string;
  start_date?: string;
  expiry_date?: string;
  redirect_correspondence: boolean;
};
type AddressAssignment = {
  id: string;
  address_id: string;
  purpose: string;
  status: string;
  verification_status: string;
  is_primary: boolean;
  effective_from?: string;
  effective_to?: string;
  line_1: string;
  line_2?: string;
  line_3?: string;
  town_city: string;
  county?: string;
  postcode: string;
  country_code: string;
  evidence_reference?: string;
};
type PropertyAddress = {
  role_assignment_id: string;
  role_label: string;
  premises_id: string;
  premises_name: string;
  line_1: string;
  line_2?: string;
  town_city: string;
  postcode: string;
};
type GroupedPropertyAddress = Omit<
  PropertyAddress,
  "role_assignment_id" | "role_label"
> & { roleLabels: string[] };
type PremisesParty = {
  id: string;
  status: string;
  verification_status: string;
  role_code: string;
  role_label: string;
  person_id?: string;
  organisation_id?: string;
  contact_name: string;
  contact_points: Array<{
    id: string;
    kind: string;
    value: string;
    purpose: string;
    status: string;
  }>;
};
type PendingPartyProposal = {
  id: string;
  proposed_name: string;
  role_code?: string;
  batch_id: string;
  batch_number: string;
  supporting_sources: Array<{
    itemId: string;
    fileName: string;
    folderPath?: string;
    evidence?: Array<{ field?: string; page?: number; passage: string }>;
  }>;
};
type ContactSearchResult = {
  id: string;
  type: "Person" | "Organisation";
  name: string;
  status: string;
};
type RoleDefinition = {
  code: string;
  label: string;
  applicable_to: "Person" | "Organisation" | "Both";
  requires_premises: boolean;
};
type LegacyRelationship = {
  id: string;
  relationship_type: string;
  status: string;
  relationship_group?: string;
  parties: Array<{
    personId?: string;
    personName?: string;
    organisationId?: string;
    organisationName?: string;
    partyRole: string;
  }>;
};
type EntityRecord = {
  building?: string;
  id: string;
  title?: string;
  salutation?: string;
  given_names?: string;
  family_name?: string;
  display_name?: string;
  legal_name?: string;
  trading_name?: string;
  company_number?: string;
  organisation_type?: string;
  premises_code?: string;
  status: string;
  contacts: Contact[];
  roles?: PartyRole[];
  otherBuildingRoles?: PartyRole[];
  memberships?: Membership[];
  representations?: Representation[];
  relationships?: LegacyRelationship[];
  partyRoles?: PremisesParty[];
  pendingPartyProposals?: PendingPartyProposal[];
  peepProfileExists?: boolean;
  addresses?: AddressAssignment[];
  propertyAddresses?: PropertyAddress[];
};

const recordNames: Record<RecordType, string> = {
  person: "Person",
  organisation: "Organisation",
  premises: "Premises",
};

function verificationBadge(status: string) {
  return <>{/verified|approved/i.test(status) && !/unverified/i.test(status) && <Icon name="circle-check"/>}{status}</>;
}

function formatDate(value?: string): string {
  return formatDateOnly(value, "Start not recorded");
}

function premisesPartyStatus(role: PremisesParty) {
  return role.verification_status === "Verified" ? "Confirmed" : "Needs review";
}

function ContactPointEditor({
  kind,
  label,
  contacts,
  entityType,
  entityId,
  onSaved,
}: {
  kind: "Email" | "Telephone";
  label: string;
  contacts: Contact[];
  entityType: RecordType;
  entityId: string;
  onSaved: () => Promise<void>;
}) {
  const [scopeParams]=useSearchParams(),contactBuilding=scopeParams.get("building")??undefined;
  const [currentId, setCurrentId] = useState("");
  const [value, setValue] = useState("");
  const [purpose, setPurpose] = useState(
    kind === "Telephone" ? "Mobile" : "General",
  );
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const relevant = contacts.filter((contact) => contact.contact_kind === kind);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      const current = relevant.find((contact) => contact.id === currentId);
      await api(withBuildingScope(`/api/contacts/${entityType}/${entityId}/contact-points`,contactBuilding), {
        method: "POST",
        body: JSON.stringify({
          contactKind: kind,
          value,
          purpose: kind === "Email" ? (current?.purpose ?? "General") : purpose,
          isPrimary: current?.is_primary ?? relevant.length === 0,
          replaceContactPointId: currentId || undefined,
        }),
      });
      setValue("");
      setCurrentId("");
      setMessage(
        `${label} saved. Previous values remain available for restore.`,
      );
      await onSaved();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : `Unable to save ${label.toLowerCase()}`,
      );
    }
  };
  const restore = async (contact: Contact) => {
    setError("");
    try {
      await api(withBuildingScope(`/api/contact-points/${contact.id}/restore`,contactBuilding), {
        method: "POST",
        body: JSON.stringify({}),
      });
      setMessage("Previous value restored and recorded in the audit history.");
      await onSaved();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Unable to restore the previous value",
      );
    }
  };
  return (
    <section className="mobilisation-contact-methods">
      <h3>{label}</h3>
      {message && <p className="success">{message}</p>}
      {error && <p className="warning">{error}</p>}
      <div className="contact-list">
        {relevant.map((contact) => (
          <div className="contact" key={contact.id}>
            <div>
              <strong>{contact.original_value}</strong>
              <span>
                {contact.purpose}
                {contact.is_primary ? " · Primary" : ""}
              </span>
            </div>
            <div>
              <span className="badge">{verificationBadge(contact.status)}</span>
              {contact.supersedes_contact_point_id && (
                <button
                  className="secondary"
                  type="button"
                  onClick={() => void restore(contact)}
                >
                  Restore previous
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <form className="proposal-form" onSubmit={save}>
        <label>
          {relevant.length ? "Add new or replace" : "Add"} {label.toLowerCase()}
          <select
            value={currentId}
            onChange={(event) => setCurrentId(event.target.value)}
          >
            <option value="">Add another</option>
            {relevant.map((contact) => (
              <option key={contact.id} value={contact.id}>
                Replace {contact.original_value}
              </option>
            ))}
          </select>
        </label>
        {kind === "Telephone" && (
          <label>
            Phone type
            <select
              value={purpose}
              onChange={(event) => setPurpose(event.target.value)}
            >
              <option>Mobile</option>
              <option>Landline</option>
              <option>General</option>
            </select>
          </label>
        )}
        <label>
          {label}
          <input
            type={kind === "Email" ? "email" : "tel"}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            required
          />
        </label>
        <button>Save {label.toLowerCase()}</button>
      </form>
    </section>
  );
}

export function UnifiedContactEditor({
  contacts,
  addresses,
  globalContext=false,
  entityType,
  entityId,
  onSaved,
}: {
  contacts: Contact[];
  addresses: AddressAssignment[];
  globalContext?:boolean;
  entityType: "person" | "organisation";
  entityId: string;
  onSaved: () => Promise<void>;
}) {
  const [scopeParams]=useSearchParams(),contactBuilding=globalContext?undefined:scopeParams.get("building")??undefined;
  const existing = (kind: string, purpose?: string) =>
    contacts.find(
      (item) =>
        item.status!=="Superseded" && item.contact_kind === kind && (!purpose || item.purpose === purpose),
    )?.original_value ?? "";
  const [email, setEmail] = useState(existing("Email"));
  const [telephone, setTelephone] = useState(existing("Telephone", "General") || existing("Telephone", "Landline"));
  const [mobile, setMobile] = useState(existing("Telephone", "Mobile"));
  const currentAddress = addresses.find(item => item.purpose==='Correspondence'&&item.is_primary&&item.status.startsWith('Current')) ?? addresses.find(item=>item.purpose==='Correspondence'&&item.status.startsWith('Current'));
  const [line1, setLine1] = useState(currentAddress?.line_1 ?? "");
  const [line2, setLine2] = useState(currentAddress?.line_2 ?? "");
  const [townCity, setTownCity] = useState(currentAddress?.town_city ?? "");
  const [county, setCounty] = useState(currentAddress?.county ?? "");
  const [postcode, setPostcode] = useState(currentAddress?.postcode ?? "");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const dirty =
    email !== existing("Email") ||
    telephone !== (existing("Telephone", "General") || existing("Telephone", "Landline")) ||
    mobile !== existing("Telephone", "Mobile") ||
    line1 !== (currentAddress?.line_1 ?? "") || line2 !== (currentAddress?.line_2 ?? "") ||
    townCity !== (currentAddress?.town_city ?? "") || county !== (currentAddress?.county ?? "") || postcode !== (currentAddress?.postcode ?? "");
  useEffect(() => {
    setEmail(existing("Email"));
    setTelephone(existing("Telephone", "General") || existing("Telephone", "Landline"));
    setMobile(existing("Telephone", "Mobile"));
    setLine1(currentAddress?.line_1 ?? ""); setLine2(currentAddress?.line_2 ?? "");
    setTownCity(currentAddress?.town_city ?? ""); setCounty(currentAddress?.county ?? ""); setPostcode(currentAddress?.postcode ?? "");
  }, [contacts, addresses]);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving || !dirty) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const pointsChanged=email!==existing('Email')||telephone!==(existing('Telephone','General')||existing('Telephone','Landline'))||mobile!==existing('Telephone','Mobile');
      if(pointsChanged)await api(
        withBuildingScope(`/api/contacts/${entityType}/${entityId}/contact-points/batch`,contactBuilding),
        {
          method: "POST",
          body: JSON.stringify({
            email: email || undefined,
            telephone: telephone || undefined,
            mobile: mobile || undefined,
          }),
        },
      );
      const addressChanged = line1 !== (currentAddress?.line_1 ?? "") || line2 !== (currentAddress?.line_2 ?? "") || townCity !== (currentAddress?.town_city ?? "") || county !== (currentAddress?.county ?? "") || postcode !== (currentAddress?.postcode ?? "");
      if (addressChanged && (line1 || townCity || postcode)) await api(withBuildingScope(`/api/contacts/${entityType}/${entityId}/addresses`,contactBuilding), { method: "POST", body: JSON.stringify({ purpose: currentAddress?.purpose ?? "Correspondence", line1, line2: line2 || undefined, townCity, county: county || undefined, postcode, countryCode: "GB", isPrimary: true }) });
      await onSaved();
      setMessage(
        `Contact information saved at ${formatDateTime(new Date())}.`,
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Unable to save contact information. Retry when ready.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <form className="proposal-form unified-contact-form" onSubmit={save}>
      {message && <p className="success wide-field">{message}</p>}
      {error && (
        <p className="warning wide-field" role="alert">
          {error}
        </p>
      )}
      <p className="form-help wide-field">
        {dirty ? "Unsaved contact changes" : "No unsaved contact changes"}
      </p>
      <label>
        Email address
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>
      <label>
        Phone
        <input
          type="tel"
          value={telephone}
          onChange={(event) => setTelephone(event.target.value)}
        />
      </label>
      <label>
        Mobile
        <input
          type="tel"
          value={mobile}
          onChange={(event) => setMobile(event.target.value)}
        />
      </label>
      <fieldset className="wide-field"><legend>Correspondence address</legend>
        <label>Address line 1<input value={line1} onChange={event=>setLine1(event.target.value)} /></label>
        <label>Address line 2<input value={line2} onChange={event=>setLine2(event.target.value)} /></label>
        <label>Town or city<input value={townCity} onChange={event=>setTownCity(event.target.value)} /></label>
        <label>County<input value={county} onChange={event=>setCounty(event.target.value)} /></label>
        <label>Postcode<input value={postcode} onChange={event=>setPostcode(event.target.value)} /></label>
      </fieldset>
      <button disabled={saving || !dirty}>
        {saving ? "Saving changes…" : "Save changes"}
      </button>
    </form>
  );
}

function PremisesRelationshipEditor({
  premisesId,
  onSaved,
}: {
  premisesId: string;
  onSaved: () => Promise<void>;
}) {
  const [mode, setMode] = useState<"Existing" | "New">("Existing");
  const [partyType, setPartyType] = useState<"Person" | "Organisation">(
    "Person",
  );
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ContactSearchResult[]>([]);
  const [selected, setSelected] = useState("");
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    api<RoleDefinition[]>("/api/role-definitions")
      .then((items) => setRoles(items.filter((item) => item.requires_premises)))
      .catch(() => setRoles([]));
  }, []);
  const search = async () => {
    setError("");
    try {
      const items = await api<ContactSearchResult[]>(
        `/api/contacts?q=${encodeURIComponent(query)}&limit=20`,
      );
      setResults(items.filter((item) => item.type === partyType));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Search failed");
    }
  };
  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    const data = new FormData(event.currentTarget);
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await api(`/api/premises/${premisesId}/relationships`, {
        method: "POST",
        body: JSON.stringify({
          partyMode: mode,
          partyType,
          partyId: mode === "Existing" ? selected : undefined,
          roleCode: data.get("roleCode"),
          startDate: data.get("startDate") || undefined,
          endDate: data.get("endDate") || undefined,
          givenNames: data.get("givenNames") || undefined,
          familyName: data.get("familyName") || undefined,
          legalName: data.get("legalName") || undefined,
          email: data.get("email") || undefined,
          telephone: data.get("telephone") || undefined,
        }),
      });
      await onSaved();
      setMessage(
        `Relationship saved at ${formatDateTime(new Date())}.`,
      );
      event.currentTarget.reset();
      setSelected("");
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Unable to save the relationship. Retry when ready.",
      );
    } finally {
      setSaving(false);
    }
  };
  const applicable = roles.filter(
    (role) => role.applicable_to === "Both" || role.applicable_to === partyType,
  );
  return (
    <section className="panel proposal-panel">
      <h2>Contacts &amp; relationships</h2>
      <p>
        Link a named person or organisation to this property. Contact details
        belong to that party, not anonymously to the flat.
      </p>
      {message && <p className="success">{message}</p>}
      {error && (
        <p className="warning" role="alert">
          {error}
        </p>
      )}
      <form className="proposal-form" onSubmit={save}>
        <label>
          Use an existing or new contact
          <select
            value={mode}
            onChange={(event) => setMode(event.target.value as typeof mode)}
          >
            <option>Existing</option>
            <option>New</option>
          </select>
        </label>
        <label>
          Party type
          <select
            value={partyType}
            onChange={(event) => {
              setPartyType(event.target.value as typeof partyType);
              setSelected("");
              setResults([]);
            }}
          >
            <option>Person</option>
            <option>Organisation</option>
          </select>
        </label>
        {mode === "Existing" ? (
          <>
            <label className="wide-field">
              Search existing contacts
              <div className="inline-search">
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Name, email or organisation"
                />
                <button
                  type="button"
                  className="secondary"
                  onClick={() => void search()}
                >
                  Search
                </button>
              </div>
            </label>
            <label className="wide-field">
              Select contact
              <select
                value={selected}
                onChange={(event) => setSelected(event.target.value)}
                required
              >
                <option value="">Choose a named contact</option>
                {results.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} · {item.type}
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : partyType === "Person" ? (
          <>
            <label>
              First and middle names
              <input name="givenNames" required />
            </label>
            <label>
              Surname
              <input name="familyName" required />
            </label>
          </>
        ) : (
          <label className="wide-field">
            Legal name
            <input name="legalName" required />
          </label>
        )}
        <label>
          Relationship role
          <select name="roleCode" required defaultValue="">
            <option value="">Choose role</option>
            {applicable.map((role) => (
              <option key={role.code} value={role.code}>
                {role.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Effective from
          <DateOnlyInput name="startDate" />
        </label>
        <label>
          Effective to
          <DateOnlyInput name="endDate" />
        </label>
        {mode === "New" && (
          <>
            <label>
              Email (optional)
              <input type="email" name="email" />
            </label>
            <label>
              Phone (optional)
              <input type="tel" name="telephone" />
            </label>
          </>
        )}
        <p className="form-help wide-field">
          {saving
            ? "Saving the named relationship…"
            : "Review the name, role and dates before saving."}
        </p>
        <button disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
      </form>
    </section>
  );
}

export function RecordDetail() {
  const { type, id } = useParams();
  const [searchParams,setSearchParams] = useSearchParams();
  const building=searchParams.get("building")??undefined;
  const relationshipBuilding=searchParams.get("relationships")==="building"?building:undefined;
  const canEdit=useCapability("contacts:edit-routine");
  const isEditing = searchParams.get("mode") === "edit"&&canEdit;
  const validType =
    type === "person" || type === "organisation" || type === "premises"
      ? type
      : undefined;
  const [record, setRecord] = useState<EntityRecord>();
  const [preferenceSummary,setPreferenceSummary]=useState<ContactPreferenceSnapshot>();
  const [error, setError] = useState<unknown>();
  const [saved, setSaved] = useState<string>();

  const recordPath =
    validType === "person"
      ? "people"
      : validType === "premises"
        ? "premises"
        : validType === "organisation"
          ? "organisations"
          : undefined;
  const recordUrl=building&&validType!=="premises"?withBuildingScope(`/api/mobilisation-contacts/${validType}/${id}/card?relationships=${relationshipBuilding?'building':'portfolio'}`,building):`/api/${recordPath}/${id}`;
  const readRecord=async()=>{const value=await api<EntityRecord>(recordUrl);if(building&&(value.id!==id||value.building!==building))throw Error("Contact card does not match the selected building.");return value;};
  const refreshRecord = async () => {
    if (!recordPath || !id || building&&validType==="premises") return;
    setRecord(await readRecord());
  };
  useEffect(() => {
    if (!recordPath || !id || building&&validType==="premises") return;
    let active=true;setRecord(undefined);setError(undefined);
    readRecord()
      .then(value=>{if(active)setRecord(value)})
      .catch(reason=>{if(active)setError(reason)});
    return()=>{active=false};
  }, [recordPath, id, recordUrl, building, validType]);

  const title =
    record?.display_name ??
    record?.trading_name ??
    record?.legal_name ??
    record?.premises_code;
  const groupedPropertyAddresses = useMemo<GroupedPropertyAddress[]>(() => {
    const grouped = new Map<string, GroupedPropertyAddress>();
    for (const address of record?.propertyAddresses ?? []) {
      const current = grouped.get(address.premises_id);
      if (current) {
        if (!current.roleLabels.includes(address.role_label))
          current.roleLabels.push(address.role_label);
      } else
        grouped.set(address.premises_id, {
          premises_id: address.premises_id,
          premises_name: address.premises_name,
          line_1: address.line_1,
          ...(address.line_2 ? { line_2: address.line_2 } : {}),
          town_city: address.town_city,
          postcode: address.postcode,
          roleLabels: [address.role_label],
        });
    }
    return [...grouped.values()];
  }, [record?.propertyAddresses]);

  // Relationships come from canonical party roles; an address projection may be empty.
  const propertyRoles = (record?.roles ?? []).filter(role => role.premises_id && (!relationshipBuilding || role.development_id === relationshipBuilding));
  const previousPropertyRoles = propertyRoles.filter(role => role.status === "Ended" || role.status === "Rejected");
  const recordedPropertyRoles = propertyRoles.filter(role => role.status !== "Ended" && role.status !== "Rejected");
  const propertyRoleSummary = (role: PartyRole) => {
    const roleBuilding = role.development_id;
    const path = roleBuilding ? `/buildings/${roleBuilding}/properties/${role.premises_id}` : `/records/premises/${role.premises_id}`;
    return <p key={role.id}><Link to={withBuildingScope(path, roleBuilding)}>{role.premises_name || "Recorded property"}</Link>{!relationshipBuilding&&role.development_name?` · ${role.development_name}`:""} · {role.role_label} · {role.status}{role.start_date && ` · from ${formatDate(role.start_date)}`}{role.end_date && ` · to ${formatDate(role.end_date)}`}{role.legal_names?.map(name=><small key={name}>Legal owner expression: {name}</small>)}</p>;
  };

  if (!validType || !id)
    return <State kind="not-found" tone="neutral" heading="Record not found" message="This record type is not available." primaryAction={{ label: "Return home", href: "/" }} />;
  if(building&&validType==="premises")return <State kind="unavailable" tone="unavailable" heading="Building view is not available here yet" message="Use the building premises workspace. The selected building is preserved." focusOnMount/>;
  if (error) return <ErrorState error={error} />;
  if (!record) return <Loading />;

  const relationships = (validType === "person" || validType === "organisation") && (
            <section className="panel">
              <h2>Management and governance</h2>
              {!!record.otherBuildingRoles?.length&&<details><summary>Other permitted building relationships</summary><ul>{record.otherBuildingRoles.map(role=><li key={role.id}><Link to={`/contacts?building=${role.development_id}`}>{role.development_name}</Link> · {role.premises_name??"Building"} · {role.role_label}{role.legal_names?.map(name=><p key={name}>Legal name: {name}</p>)}</li>)}</ul></details>}
              {building&&canEdit&&(validType==="person"||validType==="organisation")&&<details><summary>Add premises relationship</summary><ManualMobilisationContact building={building} existingContact={{id:id!,type:validType==="person"?"Person":"Organisation",name:title??""}} onSaved={()=>{setSaved("Relationship saved.");void refreshRecord().catch(setError)}}/></details>}

              {!record.roles?.length &&
              !record.memberships?.length &&
              !record.representations?.length ? (
                <Empty>
                  No roles, appointments or representation authorities are
                  recorded.
                </Empty>
              ) : (
                <>
                  {!!record.roles?.some(role=>!role.premises_id) && (
                    <>
                      <h3>Property, management and professional roles</h3>
                      <div className="contact-list">
                        {record.roles.filter(role=>!role.premises_id).map((role) => (
                          <div className="contact" key={role.id}>
                            <div>
                              <strong>{role.role_label}</strong>
                              {role.legal_names?.map(name=><span key={name}>Legal name: {name}</span>)}
                              <span>
                                {role.category} ·{" "}
                                {role.development_name ?? "Portfolio"}
                                {role.premises_name
                                  ? ` · ${role.premises_name}`
                                  : ""}
                              </span>
                            </div>
                            <span className="badge">
                              {verificationBadge(role.status)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {!!record.memberships?.length && (
                    <>
                      <h3>Organisations and appointments</h3>
                      <div className="contact-list">
                        {record.memberships.map((membership) => (
                          <div className="contact" key={membership.id}>
                            <div>
                              <strong>{membership.membership_role}</strong>
                              <span>
                                {membership.organisation_name ??
                                  membership.person_name}{" "}
                                {membership.start_date ? ` · from ${formatDate(membership.start_date)}` : ""}
                                {membership.end_date
                                  ? ` to ${formatDate(membership.end_date)}`
                                  : ""}
                              </span>
                            </div>
                            <span className="badge">
                              {verificationBadge(membership.status)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {!!record.representations?.length && (
                    <>
                      <h3>Representation and authority</h3>
                      <div className="contact-list">
                        {record.representations.map((authority) => (
                          <div className="contact" key={authority.id}>
                            <div>
                              <strong>{authority.authority_nature}</strong>
                              <span>
                                {authority.representative_name ??
                                  "Representative"}
                                {authority.expiry_date
                                  ? ` · expires ${formatDate(authority.expiry_date)}`
                                  : ""}
                                {authority.redirect_correspondence
                                  ? " · correspondence redirected"
                                  : ""}
                              </span>
                            </div>
                            <span className="badge">
                              {verificationBadge(authority.status)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </section>
          );
  const addresses = (validType === "person" || validType === "organisation") && (
            <section className="panel">
              <h2>Addresses</h2>
              <h3>Property Addresses</h3>
              {!groupedPropertyAddresses.length ? (
                <Empty>No current property addresses are recorded.</Empty>
              ) : (
                <div className="contact-list">
                  {groupedPropertyAddresses.map((address) => (
                    <div className="contact" key={address.premises_id}>
                      <div>
                        <strong>{address.premises_name}</strong>
                        <span>
                          {[
                            address.line_1,
                            address.line_2,
                            address.town_city,
                            address.postcode,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </div>
                      <span className="badge">
                        {address.roleLabels.join(" · ")} · Read only
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <h3>Contact and legal addresses</h3>
              {!record.addresses?.length ? (
                <Empty>
                  No correspondence, legal, residential or registered-office
                  assignments are recorded.
                </Empty>
              ) : (
                <div className="contact-list">
                  {record.addresses.map((address) => (
                    <div className="contact" key={address.id}>
                      <div>
                        <strong>
                          {address.purpose}
                          {address.is_primary ? " · Primary" : ""}
                        </strong>
                        <span>
                          {[
                            address.line_1,
                            address.line_2,
                            address.line_3,
                            address.town_city,
                            address.county,
                            address.postcode,
                            address.country_code,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </div>
                      <span className="badge">
                        {verificationBadge(
                          `${address.status} · ${address.verification_status}`,
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
  return (
    <div className="contact-record-workspace">
      <div className="page-heading">
        <div>
          <h1>{title}</h1>
          <p>
            {recordNames[validType]} record · {record.status}
          </p>
        </div>
        <div className="page-actions"><Link className="back-link" to="/search">Search</Link>
          {isEditing ? (
            <span className="badge">Editing · save changes before leaving</span>
          ) : canEdit ? (
            <Link
              className="button-link"
              to={`/records/${validType}/${id}?mode=edit`}
            >
              Edit record
            </Link>
          ) : null}
        </div>
      </div>
      {record.peepProfileExists&&<p className="warning contact-safety-notice">Restricted safety record exists. Details require separate PEEP permission.</p>}
      {saved&&<p role="status">{saved}</p>}
      {!isEditing&&building&&validType!=="premises"&&<label className="contact-relationship-view">Relationship view<select aria-label="Relationship view" value={relationshipBuilding?'building':'portfolio'} onChange={event=>{const next=new URLSearchParams(searchParams);next.set('relationships',event.target.value);setSearchParams(next)}}><option value="portfolio">All permitted buildings and organisations</option><option value="building">Selected building only</option></select></label>}
      {!isEditing && (validType === "premises" ? <CorrespondenceHistory entityType={validType} entityId={id} building={building}/> :
        <div className="contact-profile-layout record-column"><ContactProfileOverview contacts={record.contacts} roles={recordedPropertyRoles} id={id!} building={building} preference={preferenceSummary}/>
          <aside className="contact-profile-aside" aria-label="Contact and relationships">
            <details className="contact-full-details"><summary>All contact points and previous property roles</summary><section className="panel record-contact-essentials" aria-label="Essential contact details">
              <h2>Contact details</h2>
              {record.contacts.filter(c=>c.status!=='Superseded').map(c=><p key={c.id}><strong>{c.contact_kind}</strong><br/>{c.original_value}{c.is_primary?' · Primary':''}{c.purpose&&c.purpose!=='General'?' · '+c.purpose:''}</p>)}
              <h3>Property relationships</h3>
              {!propertyRoles.length&&<p>No property relationships are recorded in this view.</p>}
              {recordedPropertyRoles.map(propertyRoleSummary)}
              {!!previousPropertyRoles.length&&<details><summary>Previous or rejected property relationships ({previousPropertyRoles.length})</summary>{previousPropertyRoles.map(propertyRoleSummary)}</details>}
            </section>
            </details><details id="contact-governance" tabIndex={-1} className="contact-governance"><summary>Management, organisations and governance</summary>{relationships}</details>
          </aside>
          <div id="contact-correspondence" tabIndex={-1}><CorrespondenceHistory compact entityType={validType} entityId={id} building={building}/></div>
          <details tabIndex={-1} id="contact-addresses" className="record-administration"><summary>Addresses</summary>{addresses}</details>
          <details tabIndex={-1} id="contact-preferences" className="record-administration"><summary>Status, relationship history and communication preferences</summary><ContactLifecycle onSummary={setPreferenceSummary} type={validType} id={id} contacts={record.contacts??[]} addresses={record.addresses??[]} onSaved={refreshRecord}/></details>
        </div>)}
      {isEditing&&validType!=="premises"&&<details className="record-administration" open><summary>Status, relationship history and communication preferences</summary><ContactLifecycle type={validType} id={id} contacts={record.contacts??[]} addresses={record.addresses??[]} onSaved={refreshRecord}/></details>}
      {!isEditing && (
        <>
      {validType === "premises" && (
        <section className="panel">
              <h2>Contacts &amp; relationships</h2>
              <p>
                Named leaseholders, residents, occupiers and representatives
                linked to this property.
              </p>
              {!record.partyRoles?.length && !record.relationships?.length ? (
                <Empty>
                  No named contacts are currently associated with this property.
                  Any legacy anonymous premises contact points are preserved for
                  review and are not treated as people.
                </Empty>
              ) : (
                <div className="contact-list">
                  {record.partyRoles?.map((role) => {
                    const hasTelephone = role.contact_points.some(
                      (point) => point.kind === "Telephone",
                    );
                    return (
                      <div className="contact premises-party" key={role.id}>
                        <div>
                          <strong>
                            <Link
                              to={`/records/${role.person_id ? "person" : "organisation"}/${role.person_id ?? role.organisation_id}`}
                            >
                              {role.contact_name}
                            </Link>
                          </strong>
                          <span>
                            {role.person_id ? "Person" : "Organisation"} ·{" "}
                            {role.role_label}
                          </span>
                          <div className="contact-point-list">
                            {role.contact_points.map((point) => (
                              <span key={point.id}>
                                <b>{point.kind}</b> {point.value}
                                {point.purpose && point.purpose !== "General"
                                  ? ` · ${point.purpose}`
                                  : ""}
                              </span>
                            ))}
                            {!hasTelephone && (
                              <span className="missing-contact">
                                <b>Phone</b> Not recorded
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="badge">
                          {verificationBadge(premisesPartyStatus(role))}
                        </span>
                      </div>
                    );
                  })}
                  {record.relationships?.flatMap((relationship) =>
                    relationship.parties.map((party, index) => (
                      <div
                        className="contact"
                        key={`${relationship.id}-${index}`}
                      >
                        <div>
                          <strong>
                            <Link
                              to={`/records/${party.personId ? "person" : "organisation"}/${party.personId ?? party.organisationId}`}
                            >
                              {party.personName ?? party.organisationName}
                            </Link>
                          </strong>
                          <span>
                            {party.partyRole || relationship.relationship_type}
                            {relationship.relationship_group
                              ? ` · ${relationship.relationship_group}`
                              : ""}
                          </span>
                        </div>
                        <span className="badge">
                          {verificationBadge(relationship.status)}
                        </span>
                      </div>
                    )),
                  )}
                </div>
              )}
              {!!record.pendingPartyProposals?.length && (
                <div className="warning-panel">
                  <h3>{record.pendingPartyProposals.length} imported contact {record.pendingPartyProposals.length === 1 ? "link needs" : "links need"} confirmation</h3>
                  <p>These names were suggested from uploaded evidence but have not yet been confirmed as leaseholders, residents or representatives. They are not silently added to the property record.</p>
                  <div className="contact-list">
                    {record.pendingPartyProposals.map((proposal) => (
                      <div className="contact pending-contact-evidence" key={proposal.id}>
                        <div>
                          <strong>{proposal.proposed_name}</strong><span>{proposal.role_code ?? "Relationship to confirm"} · {proposal.batch_number}</span>
                          <details className="entity-evidence">
                            <summary>Review supporting evidence</summary>
                            {proposal.supporting_sources.map((source) => (
                              <div key={source.itemId}>
                                <strong>{source.fileName}</strong>
                                {source.folderPath ? <small>{source.folderPath}</small> : null}
                                {source.evidence?.slice(0, 3).map((evidence, index) => (
                                  <blockquote key={index}>{evidence.page ? `Page ${evidence.page}: ` : ""}&ldquo;{evidence.passage}&rdquo;</blockquote>
                                ))}
                              </div>
                            ))}
                          </details>
                        </div>
                        <Link to={`/documents/ingestion/${proposal.batch_id}#people-and-organisations`}>Review and confirm</Link>
                      </div>
                    ))}
                  </div>
                </div>
              )}
        </section>
      )}
      {validType === "premises" && record.contacts.length > 0 && (
        <section className="panel warning-panel">
          <h2>Legacy anonymous premises contact points</h2>
          <p>These details are preserved for review. Link them to a named person or organisation before operational use; they are not treated as a leaseholder or resident record.</p>
          <div className="contact-list">{record.contacts.map(point=><div className="contact" key={point.id}><div><strong>{point.original_value}</strong><span>{point.contact_kind} · {point.purpose}</span></div><span className="badge">Needs relationship review</span></div>)}</div>
        </section>
      )}
          {validType === "premises" && <RelatedDocuments premisesId={id} />}
          {validType === "person" && <RelatedDocuments personId={id} />}
          {validType === "organisation" && <OrganisationProcessingDefault key={id} id={id}/>}
          {validType === "organisation" && <RelatedDocuments organisationId={id} />}
          {!building&&<TimelinePanel
            {...(validType === "person"
              ? { personId: id }
              : validType === "organisation"
                ? { organisationId: id }
                : { premisesId: id })}
          />}
        </>
      )}
      {isEditing && (
        <div className="edit-workspace">
          <h2>Edit record</h2>
          <p>
            Each section has one explicit save action. A visible success message
            confirms the server transaction; unsaved fields are not applied.
          </p>

          {(validType === "person" || validType === "organisation") && (
            <ContactIdentityEditor key={`${validType}:${id}`} type={validType} id={id} building={building} record={record} onSaved={refreshRecord}/>
          )}
          {(validType === "person" || validType === "organisation") && (
            <RoleProposalForm
              entityType={recordNames[validType] as "Person" | "Organisation"}
              entityId={id}
              onSaved={refreshRecord}
            />
          )}
          {validType === "premises" ? (
            <PremisesRelationshipEditor
              premisesId={id}
              onSaved={refreshRecord}
            />
          ) : (
            <section className="panel proposal-panel">
              <h2>Contact information</h2>
              <p>
                Update email, phone numbers and address, then save the contact once.
              </p>
              <UnifiedContactEditor
                contacts={record.contacts}
                addresses={record.addresses ?? []}
                entityType={validType}
                entityId={id}
                onSaved={refreshRecord}
              />
              <details><summary>Manage multiple email and telephone details</summary>
                <ContactPointEditor kind="Email" label="Email addresses" contacts={record.contacts} entityType={validType} entityId={id} onSaved={refreshRecord}/>
                <ContactPointEditor kind="Telephone" label="Telephone numbers" contacts={record.contacts} entityType={validType} entityId={id} onSaved={refreshRecord}/>
              </details>
            </section>
          )}
        </div>
      )}
      <div className="page-actions bottom-actions">
        {isEditing ? (
          <>
            <span className="badge">Editing · save before leaving</span>
            <Link className="button-link" to={`/records/${validType}/${id}`}>
              Return to record
            </Link>
          </>
        ) : null}
      </div>
    </div>
  );
}
